// material
import { alpha, styled } from '@mui/material/styles';
import type { HTMLAttributes } from 'react';

// ----------------------------------------------------------------------

type LabelProps = HTMLAttributes<HTMLSpanElement> & {
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error';
  variant?: 'filled' | 'outlined' | 'ghost';
};

type LabelStyleProps = Pick<LabelProps, 'color' | 'variant'>;

const RootStyle = styled('span', {
  shouldForwardProp: (prop) => prop !== 'styleProps',
})<{ styleProps: LabelStyleProps }>(({ theme, styleProps }) => {
  const isLight = theme.palette.mode === 'light';
  const palette = theme.palette as typeof theme.palette & Record<string, any>;
  const { color, variant } = styleProps;
  const safeColor = color ?? 'default';

  const styleFilled = (color: string) => ({
    color: palette[color].contrastText,
    backgroundColor: palette[color].main,
  });

  const styleOutlined = (color: string) => ({
    color: palette[color].main,
    backgroundColor: 'transparent',
    border: `1px solid ${palette[color].main}`,
  });

  const styleGhost = (color: string) => ({
    color: palette[color][isLight ? 'dark' : 'light'],
    backgroundColor: alpha(palette[color].main, 0.16),
  });

  return {
    height: 22,
    minWidth: 22,
    lineHeight: 0,
    borderRadius: 8,
    cursor: 'default',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
    color: theme.palette.grey[800],
    fontSize: theme.typography.pxToRem(12),
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.grey[300],
    fontWeight: theme.typography.fontWeightBold,

    ...(color !== 'default'
      ? {
          ...(variant === 'filled' && { ...styleFilled(safeColor) }),
          ...(variant === 'outlined' && { ...styleOutlined(safeColor) }),
          ...(variant === 'ghost' && { ...styleGhost(safeColor) }),
        }
      : {
          ...(variant === 'outlined' && {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            border: `1px solid ${(palette.grey as any)['500_32']}`,
          }),
          ...(variant === 'ghost' && {
            color: isLight
              ? theme.palette.text.secondary
              : theme.palette.common.white,
            backgroundColor: (palette.grey as any)['500_16'],
          }),
        }),
  };
});

// ----------------------------------------------------------------------

export default function Label({
  color = 'default',
  variant = 'ghost',
  children,
  ...other
}: LabelProps) {
  return (
    <RootStyle styleProps={{ color, variant }} {...other}>
      {children}
    </RootStyle>
  );
}
